import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import BlogCard from "@/components/BlogCard";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const { t } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState("");

  useEffect(() => {
    fetchArticles();
  }, []);

  useEffect(() => {
    filterArticles();
  }, [searchQuery, selectedTag, articles]);

  const fetchArticles = async () => {
    try {
      const response = await fetch(`${API}/articles`);
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
        setFilteredArticles(data);
      }
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterArticles = () => {
    let filtered = [...articles];
    if (searchQuery) {
      filtered = filtered.filter(
        (article) =>
          article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          article.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (selectedTag) {
      filtered = filtered.filter((article) =>
        article.tags?.includes(selectedTag)
      );
    }
    setFilteredArticles(filtered);
  };

  const allTags = [...new Set(articles.flatMap((article) => article.tags || []))];

  return (
    <>
      <SEO
        title="Blog"
        description="Découvrez mes articles sur le développement logiciel, la cybersécurité et les dernières tendances technologiques."
        tags={["blog", "développement", "tech", "programmation"]}
      />
      <div className="min-h-screen pt-16 md:pt-20 pb-12 md:pb-16" data-testid="blog-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-8 md:py-12"
        >
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-3 md:mb-4">
            {t("blog.title")}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto px-4">
            {t("blog.subtitle")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 md:mb-12"
        >
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 max-w-2xl mx-auto">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t("blog.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="blog-search-input"
                className="pl-9 md:pl-10 rounded-full text-sm md:text-base"
              />
            </div>
          </div>

          {allTags.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mt-4 md:mt-6 px-2">
              <Button
                variant={selectedTag === "" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedTag("")}
                className="rounded-full text-xs md:text-sm h-8 md:h-9"
                data-testid="tag-all"
              >
                {t("projects.filterAll")}
              </Button>
              {allTags.slice(0, 10).map((tag) => (
                <Button
                  key={tag}
                  variant={selectedTag === tag ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedTag(tag)}
                  className="rounded-full text-xs md:text-sm h-8 md:h-9"
                  data-testid={`tag-${tag}`}
                >
                  {tag}
                </Button>
              ))}
            </div>
          )}
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-12 md:py-16">
            <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : filteredArticles.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredArticles.map((article, index) => (
              <BlogCard key={article.id} article={article} delay={index * 0.1} />
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 md:py-16"
          >
            <Filter className="h-12 w-12 md:h-16 md:w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground mb-2">
              {t("blog.noResults")}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              {searchQuery || selectedTag ? t("blog.noResults") : t("common.loading")}
            </p>
          </motion.div>
        )}
      </div>
    </div>
    </>
  );
};

export default BlogPage;
